"use client";

import React from "react";
import { FaCheckCircle } from "react-icons/fa";
import { IoIosApps } from "react-icons/io";
import { LuSettings, LuUsers, LuSmartphone, LuShieldCheck, LuPercent, LuShield } from "react-icons/lu";
import useTheme from "src/context/appThemeContext";

const TechStats = ({ stats = [] }) => {
  const { theme, mounted } = useTheme();
  const isDark = mounted && theme === "dark";
  const staticStats = [
    {
      icon: IoIosApps,
      value: "300+",
      label: "Projects Delivered",
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20",
      glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
    },
    {
      icon: LuUsers,
      value: "100+",
      label: "Happy Clients",
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20",
      glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
    },
    {
      icon: FaCheckCircle,
      value: "50+",
      label: "Repeat & Referral",
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20",
      glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
    },
    {
      icon: LuShieldCheck,
      value: "99.9%",
      label: "Crash-Free Apps",
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20",
      glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
    },
    {
      icon: LuPercent,
      value: "90%",
      label: "Faster Development",
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20",
      glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
    },
    {
      icon: LuShield,
      value: "100%",
      label: "Client Satisfaction",
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20",
      glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
    },
  ];

  return (
    <section className="relative w-full bg-transparent px-3! md:px-12! z-30 -mt-16! md:-mt-10! lg:-mt-16!">
      <div
        className={`relative mx-auto! w-full max-w-8xl overflow-hidden rounded-[16px] border px-6! md:px-12! py-8! backdrop-blur-md ${
          isDark ? "border-border-primary shadow-[0_0_50px_rgba(139,92,246,0.15)]" : "border-border-secondary shadow-[0_10px_40px_rgba(0,0,0,0.05)]"
        }`}
        style={{
          background: "var(--card-background)",
          boxShadow: "var(--card-shadow)",
        }}
      >
        <div className={`absolute -top-24! left-0 h-56 w-56 rounded-full blur-[100px] ${isDark ? "bg-[#00d2ff]/10" : "bg-[#00d2ff]/5"}`} />
        <div className={`absolute -bottom-24 right-0 h-56 w-56 rounded-full blur-[100px] ${isDark ? "bg-[#a855f7]/10" : "bg-[#a855f7]/5"}`} />

        <div className="relative z-10 grid grid-cols-2 gap-y-6! md:grid-cols-3 lg:grid-cols-6 gap-x-0">
          {staticStats.map((item, index) => {
            const Icon = item.icon;
            const isLast = index === staticStats.length - 1;

            return (
              <div key={index} className="relative flex items-center justify-center  md:px-6! ">
                <div className="flex items-center gap-3 transition-transform duration-300 hover:scale-105 w-full">
                  {Icon && (
                    <div
                      className={`flex w-10 h-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl border ${
                        isDark ? "border-white/10" : "border-border-primary/30"
                      } ${item.borderClass || ""} ${item.bgClass || ""} ${item.glowClass || ""}`}
                    >
                      <Icon size={22} className={item.colorClass} />
                    </div>
                  )}

                  <div className="flex flex-col">
                    <span className="text-sm font-bold leading-tight text-text-primary md:text-2xl">{item.value}</span>
                    <span className="text-[11px] md:text-[12px] font-medium leading-tight text-[var(--c-ink-2)]">{item.label}</span>
                  </div>
                </div>

                {!isLast && (
                  <div
                    className={`absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-8 ${isDark ? "bg-white/10" : "bg-border-primary/20"}
                      ${index % 2 === 0 ? "block" : "hidden"} 
                      ${index % 3 !== 2 ? "md:block" : "md:hidden"} 
                      ${index % 6 !== 5 ? "lg:block" : "lg:hidden"}
                    `}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div
          className={`pointer-events-none absolute inset-0 rounded-[16px] border ${
            isDark ? "border-white/5 shadow-[inset_0_0_30px_rgba(255,255,255,0.02)]" : "border-border-primary/10 shadow-[inset_0_0_30px_rgba(0,0,0,0.01)]"
          }`}
        />
      </div>
    </section>
  );
};

export default TechStats;
